import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

// PasswordInput component defined outside to prevent recreation on each render
const PasswordInput = React.memo(({ label, value, onChange, placeholder, disabled, required = true, error = null }) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          required={required}
          value={value}
          onChange={onChange}
          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 ${
            error 
              ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
              : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
          }`}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="current-password"
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            setShowPassword(!showPassword);
          }}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
          disabled={disabled}
          tabIndex="-1"
        >
          {showPassword ? (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
});

const Auth = () => {
  const { login, signup } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ email: '', password: '', fullName: '', confirmPassword: '' });
  
  // Error states
  const [loginErrors, setLoginErrors] = useState({});
  const [signupErrors, setSignupErrors] = useState({});
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  
  // Reset password states
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState('');

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const validateLoginForm = () => {
    const errors = {};
    
    if (!loginData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(loginData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!loginData.password) {
      errors.password = 'Password is required';
    }
    
    setLoginErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSignupForm = () => {
    const errors = {};
    
    if (!signupData.fullName || !signupData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    } else if (signupData.fullName.trim().length < 2) {
      errors.fullName = 'Full name must be at least 2 characters';
    }
    
    if (!signupData.email || !signupData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(signupData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!signupData.password) {
      errors.password = 'Password is required';
    } else if (!validatePassword(signupData.password)) {
      errors.password = 'Password must be at least 6 characters long';
    }
    
    if (!signupData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (signupData.password !== signupData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setSignupErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginErrors({});
    
    if (!validateLoginForm()) {
      return;
    }
    
    setLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast({
        title: "Welcome back!",
        description: "You've been successfully logged in.",
      });
      // Clear form
      setLoginData({ email: '', password: '' });
    } else {
      // Handle specific errors
      if (result.error.includes('Invalid email or password')) {
        setLoginErrors({ 
          email: 'Invalid email or password',
          password: 'Invalid email or password'
        });
      } else if (result.error.includes('inactive')) {
        setLoginErrors({ email: 'Your account has been deactivated' });
      } else {
        toast({
          title: "Login Failed",
          description: result.error,
          variant: "destructive",
        });
      }
    }
    
    setLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupErrors({});
    
    if (!validateSignupForm()) {
      return;
    }
    
    setLoading(true);
    
    const result = await signup(signupData.email, signupData.password, signupData.fullName);
    
    if (result.success) {
      toast({
        title: "Welcome to SnapStax!",
        description: "Your account has been created successfully.",
      });
      // Clear form
      setSignupData({ email: '', password: '', fullName: '', confirmPassword: '' });
    } else {
      // Handle specific errors
      if (result.error.includes('Email already registered')) {
        setSignupErrors({ email: 'An account with this email already exists' });
      } else {
        toast({
          title: "Signup Failed",
          description: result.error,
          variant: "destructive",
        });
      }
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    
    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError('Email is required');
      return;
    }
    
    if (!validateEmail(forgotPasswordEmail)) {
      setForgotPasswordError('Please enter a valid email address');
      return;
    }
    
    setResetLoading(true);
    
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      toast({
        title: "Reset Instructions Sent",
        description: "If your email is registered, you'll receive password reset instructions shortly. Check your console for the token (demo mode).",
      });
      
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
      
    } catch (error) {
      setForgotPasswordError(error.response?.data?.detail || "Failed to send reset link");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetPasswordError('');
    
    if (!resetToken.trim()) {
      setResetPasswordError('Reset token is required');
      return;
    }
    
    if (!newPassword) {
      setResetPasswordError('New password is required');
      return;
    }
    
    if (!validatePassword(newPassword)) {
      setResetPasswordError('Password must be at least 6 characters long');
      return;
    }
    
    setResetLoading(true);
    
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      toast({
        title: "Password Reset Successfully",
        description: "You can now log in with your new password.",
      });
      
      setShowResetPassword(false);
      setResetToken('');
      setNewPassword('');
      
    } catch (error) {
      setResetPasswordError(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <div className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
              SnapStax
            </div>
            <p className="text-sm text-gray-600 mt-1">powered by summarygpt.com</p>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Smart Receipt Tracking</h1>
          <p className="text-gray-600">Upload, extract, and manage your receipts with AI-powered insights</p>
        </div>

        <Card className="backdrop-blur-sm bg-white/80 border-0 shadow-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-xl">Get Started</CardTitle>
            <CardDescription>Sign in to your account or create a new one</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={loginData.email}
                      onChange={(e) => {
                        setLoginData({ ...loginData, email: e.target.value });
                        if (loginErrors.email) {
                          setLoginErrors({ ...loginErrors, email: '' });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        loginErrors.email 
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                      }`}
                      placeholder="Enter your email"
                      disabled={loading}
                    />
                    {loginErrors.email && <p className="mt-1 text-sm text-red-600">{loginErrors.email}</p>}
                  </div>
                  <PasswordInput
                    label="Password"
                    value={loginData.password}
                    onChange={(e) => {
                      setLoginData({ ...loginData, password: e.target.value });
                      if (loginErrors.password) {
                        setLoginErrors({ ...loginErrors, password: '' });
                      }
                    }}
                    placeholder="Enter your password"
                    disabled={loading}
                    error={loginErrors.password}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={loading}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>
                  
                  {/* Display general login errors */}
                  {Object.keys(loginErrors).length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex">
                        <div className="text-red-400 mr-2">⚠️</div>
                        <div className="text-sm text-red-700">
                          <p className="font-medium">Login failed:</p>
                          <ul className="mt-1 list-disc list-inside">
                            {Object.values(loginErrors).map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Forgot Password Link */}
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm text-emerald-600 hover:text-emerald-700 underline"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>
              </TabsContent>
              
              {/* Signup Tab */}
              <TabsContent value="signup">
                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={signupData.fullName}
                      onChange={(e) => {
                        setSignupData({ ...signupData, fullName: e.target.value });
                        if (signupErrors.fullName) {
                          setSignupErrors({ ...signupErrors, fullName: '' });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        signupErrors.fullName 
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                      }`}
                      placeholder="Enter your full name"
                      disabled={loading}
                    />
                    {signupErrors.fullName && <p className="mt-1 text-sm text-red-600">{signupErrors.fullName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={signupData.email}
                      onChange={(e) => {
                        setSignupData({ ...signupData, email: e.target.value });
                        if (signupErrors.email) {
                          setSignupErrors({ ...signupErrors, email: '' });
                        }
                      }}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                        signupErrors.email 
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                      }`}
                      placeholder="Enter your email"
                      disabled={loading}
                    />
                    {signupErrors.email && <p className="mt-1 text-sm text-red-600">{signupErrors.email}</p>}
                  </div>
                  <PasswordInput
                    label="Password"
                    value={signupData.password}
                    onChange={(e) => {
                      setSignupData({ ...signupData, password: e.target.value });
                      if (signupErrors.password) {
                        setSignupErrors({ ...signupErrors, password: '' });
                      }
                    }}
                    placeholder="Create a password (min 6 chars)"
                    disabled={loading}
                    error={signupErrors.password}
                  />
                  <PasswordInput
                    label="Confirm Password"
                    value={signupData.confirmPassword}
                    onChange={(e) => {
                      setSignupData({ ...signupData, confirmPassword: e.target.value });
                      if (signupErrors.confirmPassword) {
                        setSignupErrors({ ...signupErrors, confirmPassword: '' });
                      }
                    }}
                    placeholder="Confirm your password"
                    disabled={loading}
                    error={signupErrors.confirmPassword}
                  />
                  <Button 
                    type="submit" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                  
                  {/* Display general signup errors */}
                  {Object.keys(signupErrors).length > 0 && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex">
                        <div className="text-red-400 mr-2">⚠️</div>
                        <div className="text-sm text-red-700">
                          <p className="font-medium">Please fix the following errors:</p>
                          <ul className="mt-1 list-disc list-inside">
                            {Object.values(signupErrors).map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-600">
          <p>© 2024 SnapStax • Powered by <a href="https://summarygpt.com" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700">SummaryGPT.com</a></p>
        </div>
        
        {/* Reset Password Link */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => setShowResetPassword(true)}
            className="text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Have a reset token?
          </button>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Your Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={forgotPasswordEmail}
                onChange={(e) => {
                  setForgotPasswordEmail(e.target.value);
                  if (forgotPasswordError) {
                    setForgotPasswordError('');
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  forgotPasswordError 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                }`}
                placeholder="Enter your email address"
                disabled={resetLoading}
              />
              {forgotPasswordError && <p className="mt-1 text-sm text-red-600">{forgotPasswordError}</p>}
            </div>
            <p className="text-sm text-gray-600">
              We'll send you a password reset link if your email is registered with us.
            </p>
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForgotPassword(false)}
                disabled={resetLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={resetLoading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Reset Token</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reset Token <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={resetToken}
                onChange={(e) => {
                  setResetToken(e.target.value);
                  if (resetPasswordError) {
                    setResetPasswordError('');
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  resetPasswordError 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-emerald-500 focus:border-emerald-500'
                }`}
                placeholder="Enter your reset token"
                disabled={resetLoading}
              />
            </div>
            <PasswordInput
              label="New Password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (resetPasswordError) {
                  setResetPasswordError('');
                }
              }}
              placeholder="Enter your new password (min 6 chars)"
              disabled={resetLoading}
            />
            {resetPasswordError && <p className="mt-1 text-sm text-red-600">{resetPasswordError}</p>}
            <p className="text-sm text-gray-600">
              Enter the reset token you received and your new password.
            </p>
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowResetPassword(false)}
                disabled={resetLoading}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={resetLoading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
